/**
 * Localized default messages for Zod. Schemas that pass their own message
 * keep it; everything else (max length, number bounds, enums, NaN from an
 * empty number field) gets a translated message instead of Zod's English.
 */

import { z, type ZodErrorMap } from 'zod';

type Translate = (key: string, vars?: Record<string, unknown>) => string;

function sizeMessage(t: Translate, issue: { type: string; minimum?: unknown; maximum?: unknown }, bound: 'min' | 'max'): string {
  const value = bound === 'min' ? issue.minimum : issue.maximum;
  if (issue.type === 'string') {
    if (bound === 'min' && Number(value) === 1) return t('common:validation.required');
    return t(`common:validation.${bound === 'min' ? 'minLength' : 'maxLength'}`, { count: Number(value) });
  }
  if (issue.type === 'array') return t(`common:validation.${bound === 'min' ? 'minItems' : 'maxItems'}`, { count: Number(value) });
  return t(`common:validation.${bound === 'min' ? 'minNumber' : 'maxNumber'}`, { value: Number(value) });
}

export function createZodErrorMap(t: Translate): ZodErrorMap {
  return (issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.too_small:
        return { message: sizeMessage(t, issue, 'min') };
      case z.ZodIssueCode.too_big:
        return { message: sizeMessage(t, issue, 'max') };
      case z.ZodIssueCode.invalid_enum_value:
        return { message: t('common:validation.invalidChoice') };
      case z.ZodIssueCode.invalid_type:
        if (issue.expected === 'number') return { message: t('common:validation.invalidNumber') };
        if (issue.received === 'undefined' || issue.received === 'null') return { message: t('common:validation.required') };
        return { message: t('common:validation.invalidValue') };
      case z.ZodIssueCode.invalid_string:
        return { message: t('common:validation.invalidValue') };
      default:
        return { message: ctx.defaultError };
    }
  };
}

/** Install once at startup (messages are resolved at validation time). */
export function installZodI18n(t: Translate): void {
  z.setErrorMap(createZodErrorMap(t));
}
